import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Constants from '../../utils/Constants';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import Modal from '../Modal/Modal';
import { Selectable } from '../../utils/CommonTypes/CommonTypes';
import { IState, ReduxMap } from '../../redux/redux';
import { ServerResponse } from '../../utils/Server';
import Editor from '../GenericForms/Editor';
import { FormatDate } from '../../utils/Tools';
import Button from '../_Core/Button';
import LoadingDots from '../_Core/LoadingDots';
import BulkEmailHistory from './BulkEmailHistory';
import ScheduledBulkEmailList from './ScheduledBulkEmailList';
import { School, Tag, CombinedTagOption, IBulkEmailHistoryItem, IBulkMessageResults, IScheduledBulkMessage, IBulkEmailComposeState, IAttachmentLink, SYSTEM_DATE_OPTIONS } from './EmailToolTypes';

interface IProps extends IState {}

type ScheduleMode = 'now' | 'absolute' | 'relative';

const EXCLUSIVE_RECIPIENT_GROUPS = new Set(['outstanding', 'studentFees', 'judgesWithAssignments', 'judgesWithAssignmentsNoScores', 'judgesPlanningToParticipate']);

const PARTICIPANT_FILTER_GROUPS = new Set(['students', 'studentsTeachers', 'parents', 'firsttimemissing', 'tempParticipants', 'partyMembers']);

const INTERVIEW_KEY_GROUPS = new Set([
  'students',
  'studentsTeachers',
  'parents',
  'firsttimemissing',
  'tempParticipants',
  'judges',
  'judgesWithAssignments',
  'judgesWithAssignmentsNoScores',
  'judgesPlanningToParticipate',
]);

const SCHOOL_AFFILIATION_KEY_GROUPS = new Set([
  'SchoolAffiliationBilling',
  'SchoolAffiliationPrincipal',
  'SchoolAffiliationSchoolContact',
  'SchoolAffiliationBillingNoInvoice',
  'SchoolAffiliationPrincipalNoInvoice',
  'SchoolAffiliationSchoolContactNoInvoice',
  'SchoolAffiliationParticipantBilling',
]);

const SCHOOL_AFFILIATION_INVOICE_KEY_GROUPS = new Set([
  'SchoolAffiliationBilling',
  'SchoolAffiliationPrincipal',
  'SchoolAffiliationSchoolContact',
  'SchoolAffiliationParticipantBilling',
]);

const applyRecipientGroups = (body: Record<string, any>, recipients: { value: string; label: string }[]) => {
  const flexFlows = recipients.map((x) => String(x.value ?? '')).filter((value) => value.startsWith('flex-'));
  recipients.forEach((recipient) => {
    const value = String(recipient.value ?? '');
    if (!value.startsWith('flex-')) {
      body[value] = true;
    }
  });
  if (flexFlows.length > 0) {
    body.flexFlows = flexFlows;
  }
};

const ProcessingAlert = ({ children }: { children: React.ReactNode }) => (
  <div className="alert alert-info">
    <h3>
      <LoadingDots label="Processing" />
    </h3>
    {children}
  </div>
);

const EmailToolContainer = (props: IProps) => {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [preEmailEditorState, setPreEmailEditorState] = useState('');
  const [hideSchoolList, setHideSchoolList] = useState(true);
  const [schoolList, setSchoolList] = useState([]);
  const [selectedSchools, setSelectedSchools] = useState<Selectable[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Selectable[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Selectable[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<Selectable[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<Selectable[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<Selectable[]>([]);
  const [gradeOptions, setGradeOptions] = useState<Selectable[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<Selectable[]>([]);
  const [selectedCombinedTags, setSelectedCombinedTags] = useState<CombinedTagOption[]>([]);
  const [selectedExcludedTags, setSelectedExcludedTags] = useState<CombinedTagOption[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [validFiles, setValidFiles] = useState<any>([]);
  const [existingAttachments, setExistingAttachments] = useState<IAttachmentLink[]>([]);

  const [subjectVal, setSubjectVal] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusRequestId, setStatusRequestId] = useState<string | null>(null);
  const [personTags, setPersonTags] = useState<Tag[]>([]);
  const [projectTags, setProjectTags] = useState<Tag[]>([]);
  const [autoTags, setAutoTags] = useState<Tag[]>([]);
  const [recipientCategories, setRecipientCategories] = useState([]);
  const [flexFlowOptions, setFlexFlowOptions] = useState<Selectable[]>([]);
  const [messageResults, setMessageResults] = useState<IBulkMessageResults>();
  const [currentMessage, setCurrentMessage] = useState<number>(0);
  const [andMode, setAndMode] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);

  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [relativeSystemDateKey, setRelativeSystemDateKey] = useState('');
  const [relativeDirection, setRelativeDirection] = useState<'before' | 'after'>('before');
  const [relativeDays, setRelativeDays] = useState<number>(14);
  const [editingPublicId, setEditingPublicId] = useState<string | null>(null);

  useEffect(() => {
    store.server
      .getApi('../school/list')
      .then((res: any) => {
        setSchoolList(res.Value.schools);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });

    store.server
      .getApi('../Tag/Info')
      .then((res: any) => {
        console.log(res);
        setPersonTags(res.Value.personTags);
        setProjectTags(res.Value.projectTags);
        setAutoTags(res.Value.autoTags);
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });

    store.server
      .getApi('../Email/BulkFilterOptions')
      .then((res: ServerResponse<{ Categories: Selectable[]; Grades: Selectable[]; Divisions: Selectable[]; FlexFlows: Selectable[] }>) => {
        if (res.Success && res.Value) {
          setCategoryOptions(res.Value.Categories ?? []);
          setGradeOptions(res.Value.Grades ?? []);
          setDivisionOptions(res.Value.Divisions ?? []);
          setFlexFlowOptions(res.Value.FlexFlows ?? []);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (props.User) {
      let recipientCategories: Selectable[] = [
        { value: 'students', label: props.Settings?.text?.ParticipantLabel ?? 'Students' },
        { value: 'studentsTeachers', label: `Copy Teachers of ${props.Settings?.text?.ParticipantLabel ?? 'Students'}` },
        { value: 'parents', label: 'Parents' },
        { value: 'teachers', label: 'Teachers' },
        { value: 'studentFees', label: 'Missing Student Fees' },
        { value: 'outstanding', label: 'Outstanding Invoice' },
        { value: 'judges', label: 'Judges' },
        { value: 'judgesWithAssignments', label: 'Judges with assignments' },
        { value: 'judgesWithAssignmentsNoScores', label: 'Judges with assignments — no scores submitted' },
        ...(props.Settings?.TrackingJudgesPlanToParticipate ? [{ value: 'judgesPlanningToParticipate', label: 'Judges Planning to Participate' }] : []),
        { value: 'volunteers', label: 'Volunteers' },
        { value: 'staff', label: 'Staff' },
        { value: 'attendees', label: 'Attendees' },
        { value: 'firsttimemissing', label: `${props.Settings.text.ParticipantLabel ?? 'Students'} - Missing first time login` },
        { value: 'tempParticipants', label: `Partially Registered ${props.Settings.text.ParticipantLabel ?? 'Students'}` },
        { value: 'tempNonParticipants', label: `Partially Registered Non-${props.Settings.text.ParticipantLabel ?? 'Students'}` },
        ...(props.Settings?.SchoolAffiliateInUse
          ? [
              { value: 'SchoolAffiliationParticipantBilling', label: 'School Affiliation Participation Billing' },
              { value: 'SchoolAffiliationBilling', label: 'School Affiliation Billing Office (with invoice)' },
              { value: 'SchoolAffiliationBillingNoInvoice', label: 'School Affiliation Billing Office (no invoice)' },
              { value: 'SchoolAffiliationPrincipal', label: 'School Affiliation Principal (with invoice)' },
              { value: 'SchoolAffiliationPrincipalNoInvoice', label: 'School Affiliation Principal (no invoice)' },
              { value: 'SchoolAffiliationSchoolContact', label: 'School Affiliation School Contact (with invoice)' },
              { value: 'SchoolAffiliationSchoolContactNoInvoice', label: 'School Affiliation School Contact (no invoice)' },
            ]
          : []),
      ];
      if (props.Settings?.usesParty) {
        recipientCategories.splice(3, 0, { value: 'partyMembers', label: 'Party Members' });
      }
      if (flexFlowOptions.length > 0) {
        recipientCategories.push(...flexFlowOptions);
      }
      setRecipientCategories(recipientCategories);
    }
  }, [props.User, flexFlowOptions, props.Settings?.SchoolAffiliateInUse, props.Settings?.usesParty]);

  useEffect(() => {
    if (errorMessage) {
      toast.error(errorMessage);
      setErrorMessage('');
    }
  }, [errorMessage]);

  const stopStatusPolling = () => {
    setStatusRequestId(null);
  };

  const setModalOpenState = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      stopStatusPolling();
    }
  };

  useEffect(() => {
    let filteredArray = selectedFiles.reduce((file: any, current: any) => {
      if (current.invalid) return file;
      const x: any = file.find((item: any) => item.name === current.name);
      if (!x) {
        return [...file, current];
      } else {
        return file;
      }
    }, []);
    setValidFiles([...filteredArray]);
  }, [selectedFiles]);

  const mappedRecipients = recipientCategories.map((group) => {
    return {
      value: group.value,
      label: group.label,
    };
  });

  const mappedSchoolList = schoolList.map((school: School) => {
    return {
      value: school.id,
      label: school.name,
    };
  });

  const mappedEntryStatus = () => {
    return [
      { value: `Pending`, label: props.Settings?.text.EntryStatusPending },
      { value: `Approved`, label: props.Settings?.text.EntryStatusApproved },
      { value: `Dropped`, label: props.Settings?.text.EntryStatusDropped },
      { value: `DoNotAdvance`, label: props.Settings?.text.EntryStatusDNA },
    ];
  };

  const getMinScheduleValue = () => {
    const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
    return now.toISOString().slice(0, 16);
  };

  const onEditorStateChanged = (state) => {
    setPreEmailEditorState(state);
  };

  const toggleHideSchoolList = () => setHideSchoolList(!hideSchoolList);

  const checkEmailRegex = (emailAddress: string) => {
    return Constants.emailRegex.test(emailAddress.toLowerCase());
  };

  const onChange = (e: any) => {
    if (e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (name: string) => {
    setSelectedFiles((prev: any[]) => prev.filter((f) => f.name !== name));
  };

  const removeExistingAttachment = (fileId: string) => {
    setExistingAttachments((prev) => prev.filter((a) => a.FileId !== fileId));
  };

  const validateFile = (file: any) => {
    const validTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (validTypes.indexOf(file.type) === -1) {
      return false;
    }
    return true;
  };

  const validateFileSize = (file: any) => {
    const maxSize = 10 * 1024 * 1024; //10MB max size
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleFiles = async (files: any) => {
    let filesArr: any[] = [];
    for (let i = 0; i < files.length; i++) {
      if (validateFileSize(files[i])) {
        if (validateFile(files[i])) {
          // add to an array so we can display the name of file
          filesArr.push(files[i]);
        } else {
          // add a new property called invalid
          files[i]['invalid'] = true;
          // do some error stuff
          setErrorMessage('Not a valid format');
        }
      } else {
        files[i]['invalid'] = true;
        await setErrorMessage('Files must be < 10MB');
      }
      setSelectedFiles((prev: any[]) => [...prev, ...filesArr]);
    }
  };

  useEffect(() => {
    if (!isModalOpen || !statusRequestId) return;

    let cancelled = false;
    let attempt = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    // ~30 minutes at 2.5s intervals — bulk sends to large recipient lists can run a long time
    const maxAttempts = 720;

    const scheduleNext = (delayMs: number) => {
      if (cancelled) return;
      timeoutId = setTimeout(poll, delayMs);
    };

    const poll = () => {
      store.server
        .getApi2Quite<IBulkMessageResults>(`../Email/Status/${statusRequestId}`)
        .then((res) => {
          if (cancelled) return;
          attempt += 1;

          if (res.Success && res.Value) {
            setMessageResults(res.Value);
            setCurrentMessage(0);

            if (res.Value.Finished) {
              stopStatusPolling();
              if (!res.Value.IsPreview) {
                toast.success(
                  res.Value.ErrorsCount > 0 ? `Finished sending with ${res.Value.ErrorsCount} error(s). Sent: ${res.Value.SentCount}.` : `Message sent to ${res.Value.SentCount} recipient(s).`,
                );
                // Clear the composer; leave the modal open so results can be reviewed.
                handleAnotherClick();
              }
              return;
            }

            if (attempt < maxAttempts) {
              scheduleNext(2500);
            } else {
              toast.error('Timed out waiting for bulk email to finish. Check Bulk History shortly.');
              stopStatusPolling();
            }
            return;
          }

          // Job may not be ready yet (or a transient error). Keep polling rather than failing the UI.
          if (attempt < maxAttempts) {
            scheduleNext(2500);
          } else {
            toast.error(res.Message || 'Timed out waiting for email status.');
            stopStatusPolling();
          }
        })
        .catch((err) => {
          if (cancelled) return;
          console.log(err);
          attempt += 1;
          if (attempt < maxAttempts) {
            scheduleNext(2500);
          } else {
            toast.error(`Error checking message status. ${err}`);
            stopStatusPolling();
          }
        });
    };

    // First poll shortly after queueing so users see progress quickly
    scheduleNext(1000);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isModalOpen, statusRequestId]);

  const handlePreview = () => {
    if (selectedRecipients.length === 0) return toast.error('Need a "To" group');
    if (subjectVal === '') return toast.error('Need a Subject');
    let messageBody = preEmailEditorState;
    let checkBodyArr = messageBody.split(/[\n\r]/g);
    // the toHtml() in an empty body returns a <p></p> and a new line.
    if (checkBodyArr[0] === `<p></p>` && checkBodyArr.length === 2) return toast.error('Need Email Body content');

    let formattingReqBody = {
      schoolFilter: selectedSchools.map((school) => school.value),
      entryStatusFilter: selectedStatus?.map((x) => x.value),
      categoryFilter: selectedCategories.map((x) => x.value),
      gradeFilter: selectedGrades.map((x) => x.value),
      divisionFilter: selectedDivisions.map((x) => x.value),
      isPreview: true,
      subject: subjectVal,
      message: messageBody,
      existingAttachments,
    };

    if (selectedCombinedTags.length > 0) {
      formattingReqBody['Tags'] = selectedCombinedTags.map((x) => x.value);
      formattingReqBody['requireAllTags'] = andMode;
    }

    if (selectedExcludedTags.length > 0) {
      formattingReqBody['ExcludeTags'] = selectedExcludedTags.map((x) => x.value);
    }

    applyRecipientGroups(formattingReqBody, selectedRecipients);

    let filesArr = validFiles;

    store.server
      .postApi<ServerResponse<string>>('../Email/SendBulkEmail', formattingReqBody, filesArr)
      .then((res) => {
        if (res.Success) {
          stopStatusPolling();
          setModalOpenState(true);
          setMessageResults(undefined);
          setStatusRequestId(res.Value);
        } else {
          toast.error(res.Message);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(`Error trying to preview your message. ${err}`);
      });
  };

  const handleSendOrSchedule = () => {
    if (selectedRecipients.length === 0) return toast.error('Need a "To" group');
    if (subjectVal === '') return toast.error('Need a Subject');
    let messageBody = preEmailEditorState;
    let checkBodyArr = messageBody.split(/[\n\r]/g);
    if (checkBodyArr[0] === `<p></p>` && checkBodyArr.length === 2) return toast.error('Need Email Body content');

    let formattingReqBody: any = {
      schoolFilter: selectedSchools.map((school) => school.value),
      entryStatusFilter: selectedStatus?.map((x) => x.value),
      categoryFilter: selectedCategories.map((x) => x.value),
      gradeFilter: selectedGrades.map((x) => x.value),
      divisionFilter: selectedDivisions.map((x) => x.value),
      isPreview: false,
      subject: subjectVal,
      message: messageBody,
      scheduleMode,
      existingAttachments,
    };

    let confirmMessage = 'Are you sure you want to send this message?';

    if (scheduleMode === 'absolute') {
      if (!scheduledAt) return toast.error('Pick a date and time to schedule this message.');
      const scheduledDate = new Date(scheduledAt);
      if (isNaN(scheduledDate.getTime()) || scheduledDate.getTime() <= Date.now()) {
        return toast.error('Scheduled time must be in the future.');
      }
      formattingReqBody.scheduledAtUtc = scheduledDate.toISOString();
      confirmMessage = `Schedule this message to send on ${scheduledDate.toLocaleString()}?`;
    } else if (scheduleMode === 'relative') {
      if (!relativeSystemDateKey) return toast.error('Pick a system date to schedule relative to.');
      formattingReqBody.relativeSystemDateKey = relativeSystemDateKey;
      formattingReqBody.relativeDayOffset = relativeDirection === 'before' ? -Math.abs(relativeDays) : Math.abs(relativeDays);
      const dateLabel = SYSTEM_DATE_OPTIONS.find((x) => x.value === relativeSystemDateKey)?.label ?? relativeSystemDateKey;
      confirmMessage = `Schedule this message to send ${relativeDays} day(s) ${relativeDirection} ${dateLabel}?`;
    }

    if (editingPublicId) {
      confirmMessage = 'Save changes to this scheduled message?';
    }

    if (selectedCombinedTags.length > 0) {
      formattingReqBody['Tags'] = selectedCombinedTags.map((x) => x.value);
      formattingReqBody['requireAllTags'] = andMode;
    }

    if (selectedExcludedTags.length > 0) {
      formattingReqBody['ExcludeTags'] = selectedExcludedTags.map((x) => x.value);
    }

    applyRecipientGroups(formattingReqBody, selectedRecipients);

    if (!window.confirm(confirmMessage)) return;

    let filesArr = validFiles;
    const url = editingPublicId ? `../Email/EditScheduledBulkMessage/${editingPublicId}` : '../Email/SendBulkEmail';

    store.server
      .postApi<ServerResponse<string>>(url, formattingReqBody, filesArr)
      .then((res) => {
        if (res.Success) {
          // Send Now is queued server-side and polled via Status (same as preview).
          // Edits and future schedules return immediately — no long-running job to poll.
          if (!editingPublicId && scheduleMode === 'now') {
            stopStatusPolling();
            setModalOpenState(true);
            setMessageResults(undefined);
            setStatusRequestId(res.Value);
            toast.info('Sending in the background. Progress will update here.');
          } else {
            toast.success(editingPublicId ? 'Scheduled message updated.' : 'Message scheduled.');
            handleAnotherClick();
          }
        } else {
          toast.error(res.Message);
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error(`Error sending your message. ${err}`);
      });
  };

  const handleAnotherClick = () => {
    stopStatusPolling();
    setSelectedRecipients([]);
    setPreEmailEditorState('');
    setHideSchoolList(true);
    setSelectedFiles([]);
    setErrorMessage('');
    setValidFiles([]);
    setExistingAttachments([]);
    setSubjectVal('');
    setScheduleMode('now');
    setScheduledAt('');
    setRelativeSystemDateKey('');
    setRelativeDirection('before');
    setRelativeDays(14);
    setEditingPublicId(null);
    handleClearFilters();
  };

  const handleClearFilters = () => {
    setSelectedSchools([]);
    setSelectedCombinedTags([]);
    setSelectedExcludedTags([]);
    setSelectedStatus([]);
    setSelectedCategories([]);
    setSelectedGrades([]);
    setSelectedDivisions([]);
  };

  const mappedPersonTags: CombinedTagOption[] = personTags.map((tag) => {
    return {
      label: `${tag.Value} (Person)`,
      value: tag.Id,
      tagType: 'person',
    };
  });

  const mappedProjectTags: CombinedTagOption[] = projectTags.map((tag) => {
    return {
      label: `${tag.Value} (Project)`,
      value: tag.Id,
      tagType: 'project',
    };
  });

  const mappedAutoTags: CombinedTagOption[] = autoTags.map((tag) => {
    return {
      label: `${tag.Value} (Auto)`,
      value: tag.Id,
      tagType: 'auto',
    };
  });

  const combinedTags = [...mappedPersonTags, ...mappedProjectTags, ...mappedAutoTags];

  const applyComposeStateToForm = (composeState: IBulkEmailComposeState | undefined) => {
    if (!composeState) {
      setSelectedRecipients([]);
      handleClearFilters();
      setAndMode(false);
      setHideSchoolList(true);
      setTabIndex(0);
      return false;
    }

    const recipientGroups = new Set(composeState.RecipientGroups ?? []);
    const schoolFilter = new Set(composeState.SchoolFilter ?? []);
    const entryStatusFilter = new Set(composeState.EntryStatusFilter ?? []);
    const tagFilter = new Set(composeState.Tags ?? []);
    const excludeTagFilter = new Set(composeState.ExcludeTags ?? []);
    const categoryFilter = new Set(composeState.CategoryFilter ?? []);
    const gradeFilter = new Set((composeState.GradeFilter ?? []).map((x) => String(x)));
    const divisionFilter = new Set(composeState.DivisionFilter ?? []);

    setSelectedRecipients(mappedRecipients.filter((x) => recipientGroups.has(x.value)) as Selectable[]);
    setSelectedSchools(mappedSchoolList.filter((x) => schoolFilter.has(x.value)) as Selectable[]);
    setSelectedStatus(mappedEntryStatus().filter((x) => entryStatusFilter.has(x.value)) as Selectable[]);
    setSelectedCombinedTags(combinedTags.filter((x) => typeof x.value === 'number' && tagFilter.has(x.value)));
    setSelectedExcludedTags(combinedTags.filter((x) => typeof x.value === 'number' && excludeTagFilter.has(x.value)));
    setSelectedCategories(categoryOptions.filter((x) => categoryFilter.has(Number(x.value))));
    setSelectedGrades(gradeOptions.filter((x) => gradeFilter.has(String(x.value))));
    setSelectedDivisions(divisionOptions.filter((x) => divisionFilter.has(Number(x.value))));
    setAndMode(composeState.RequireAllTags ?? false);
    setHideSchoolList(
      (composeState.SchoolFilter?.length ?? 0) === 0 &&
        (composeState.EntryStatusFilter?.length ?? 0) === 0 &&
        (composeState.Tags?.length ?? 0) === 0 &&
        (composeState.ExcludeTags?.length ?? 0) === 0 &&
        (composeState.CategoryFilter?.length ?? 0) === 0 &&
        (composeState.GradeFilter?.length ?? 0) === 0 &&
        (composeState.DivisionFilter?.length ?? 0) === 0,
    );
    setTabIndex(0);
    return true;
  };

  const handleHistoryItemSelected = (item: IBulkEmailHistoryItem) => {
    setSubjectVal(item.Subject ?? '');
    setPreEmailEditorState(item.Message ?? '');
    setSelectedFiles([]);
    setValidFiles([]);
    setExistingAttachments(item.Attachments ?? []);
    setErrorMessage('');
    setIsModalOpen(false);
    stopStatusPolling();
    setEditingPublicId(null);
    setScheduleMode('now');
    setScheduledAt('');

    const hydrated = applyComposeStateToForm(item.ComposeState);
    if (!hydrated) {
      toast.info('Loaded subject and message. Recipient filters were not saved for this older bulk email.');
      return;
    }
    toast.info('Bulk email loaded into the composer.');
  };

  const handleEditPending = (item: IScheduledBulkMessage) => {
    setSubjectVal(item.Subject ?? '');
    setPreEmailEditorState(item.Message ?? '');
    setSelectedFiles([]);
    setValidFiles([]);
    setExistingAttachments(item.Attachments ?? []);
    setErrorMessage('');
    setIsModalOpen(false);
    stopStatusPolling();
    setEditingPublicId(item.PublicId);

    if (item.ScheduleRelationship) {
      setScheduleMode('relative');
      setRelativeSystemDateKey(item.ScheduleRelationship.SystemDateKey);
      setRelativeDirection(item.ScheduleRelationship.DayOffset < 0 ? 'before' : 'after');
      setRelativeDays(Math.abs(item.ScheduleRelationship.DayOffset));
      setScheduledAt('');
    } else if (item.ScheduledAtUtc) {
      setScheduleMode('absolute');
      setScheduledAt(FormatDate(item.ScheduledAtUtc, 'input-date-time') ?? '');
    } else {
      setScheduleMode('now');
      setScheduledAt('');
    }

    const hydrated = applyComposeStateToForm(item.ComposeState);
    if (!hydrated) {
      toast.info('Loaded subject and message. Recipient filters were not saved for this message.');
      return;
    }
    toast.info('Scheduled message loaded into the composer.');
  };

  const renderBulkMailer = () => (
    <>
      <div className="col-sm-12">
        <h3>Bulk Mailer</h3>
        Quickly and easily communicate with your students, teachers, volunteers, and staff members. Fill out the form below and we will send them all an email.
        <br />
        <br />
        {editingPublicId && (
          <div className="alert alert-info">
            Editing a pending scheduled message.{' '}
            <button className="btn btn-link" onClick={handleAnotherClick}>
              Cancel editing
            </button>
          </div>
        )}
      </div>
      <div className="form-horizontal">
        <div className="form-group row">
          <label className="control-label col-sm-3">To</label>

          <Select
            onChange={(newValue) => {
              const next = (newValue as Selectable[]) ?? [];
              const added = next.find((x) => !selectedRecipients.some((y) => y.value === x.value));
              if (added && EXCLUSIVE_RECIPIENT_GROUPS.has(String(added.value))) {
                setSelectedRecipients([added]);
                return;
              }
              setSelectedRecipients(next.filter((x) => !EXCLUSIVE_RECIPIENT_GROUPS.has(String(x.value))) as Selectable[]);
            }}
            value={selectedRecipients}
            options={mappedRecipients}
            className="col-sm-6 grow-1"
            isMulti
            isClearable={true}
            menuPlacement={'auto'}
          />
          <div className="col-sm-3">
            <button className="btn btn-default" onClick={toggleHideSchoolList}>
              <i className="fal fa-filter"></i> Toggle Additional Filters
            </button>
          </div>
        </div>
        <div className="form-group">
          {!hideSchoolList && (
            <>
              <div className="col-sm-8 col-sm-offset-2 soft-border">
                <h3>Additional Filters</h3>
                <p>
                  Only the people selected in the <strong>To</strong> field and additional filter(s) will be sent this message.
                </p>

                {selectedRecipients.some((x) => PARTICIPANT_FILTER_GROUPS.has(String(x.value))) && (
                  <>
                    {(props.Settings?.TrackEntryStatus ?? false) && (
                      <div className="row">
                        <label className="control-label col-sm-4">Entry Status</label>
                        <Select
                          onChange={(newValue) => setSelectedStatus(newValue as Selectable[])}
                          value={selectedStatus}
                          options={mappedEntryStatus()}
                          placeholder={'choose none for all entries'}
                          className="col-sm-8 grow-1"
                          isMulti
                          isClearable={true}
                          menuPlacement={'auto'}
                        />
                      </div>
                    )}
                    <div className="row">
                      <label className="control-label col-sm-4">School Filter</label>
                      <Select
                        onChange={(newValue) => setSelectedSchools(newValue as Selectable[])}
                        value={selectedSchools}
                        options={mappedSchoolList}
                        placeholder={'choose none for all schools'}
                        className="col-sm-8 grow-1"
                        isMulti
                        isClearable={true}
                        menuPlacement={'auto'}
                      />
                    </div>
                    <div className="row">
                      <label className="control-label col-sm-4">Category</label>
                      <Select
                        onChange={(newValue) => setSelectedCategories((newValue as Selectable[]) ?? [])}
                        value={selectedCategories}
                        options={categoryOptions}
                        placeholder={'choose none for all categories'}
                        className="col-sm-8 grow-1"
                        isMulti
                        isClearable={true}
                        menuPlacement={'auto'}
                      />
                    </div>
                    <div className="row">
                      <label className="control-label col-sm-4">Grade</label>
                      <Select
                        onChange={(newValue) => setSelectedGrades((newValue as Selectable[]) ?? [])}
                        value={selectedGrades}
                        options={gradeOptions}
                        placeholder={'choose none for all grades'}
                        className="col-sm-8 grow-1"
                        isMulti
                        isClearable={true}
                        menuPlacement={'auto'}
                      />
                    </div>
                    <div className="row">
                      <label className="control-label col-sm-4">Division</label>
                      <Select
                        onChange={(newValue) => setSelectedDivisions((newValue as Selectable[]) ?? [])}
                        value={selectedDivisions}
                        options={divisionOptions}
                        placeholder={'choose none for all divisions'}
                        className="col-sm-8 grow-1"
                        isMulti
                        isClearable={true}
                        menuPlacement={'auto'}
                      />
                    </div>
                  </>
                )}

                <div className="row">
                  <label className="control-label col-sm-4">Tag Filter</label>
                  <Select
                    onChange={(newValue) => setSelectedCombinedTags((newValue as CombinedTagOption[]) ?? [])}
                    value={selectedCombinedTags}
                    options={combinedTags}
                    placeholder={'People/Projects with these tags'}
                    className="col-sm-8 grow-1"
                    isMulti
                    isClearable={true}
                    menuPlacement={'auto'}
                  />
                </div>
                {selectedCombinedTags.length > 1 && (
                  <div className="col-sm-12 text-right">
                    <Button onClick={() => setAndMode(!andMode)} text={andMode ? 'Require All Tags' : 'Require Any Tag'} type={andMode ? 'on' : 'off'} />
                  </div>
                )}
                {andMode && new Set(selectedCombinedTags.map((x) => x.tagType)).size > 1 && (
                  <div className="col-sm-12 text-right text-muted">
                    <small>"Require All" only matches when every selected tag is on the same person or the same project - mixing Person/Project/Auto tags here won't match anyone.</small>
                  </div>
                )}
                <div className="row">
                  <label className="control-label col-sm-4">Exclude Tag Filter</label>
                  <Select
                    onChange={(newValue) => setSelectedExcludedTags((newValue as CombinedTagOption[]) ?? [])}
                    value={selectedExcludedTags}
                    options={combinedTags}
                    placeholder={'Exclude people/projects with these tags'}
                    className="col-sm-8 grow-1"
                    isMulti
                    isClearable={true}
                    menuPlacement={'auto'}
                  />
                </div>
                <div className="text-right">
                  <button className="btn btn-default" onClick={handleClearFilters}>
                    clear filters
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="form-group row">
          <label className="control-label col-sm-3">Subject</label>
          <div className="col-sm-8">
            <input placeholder={'Email subject'} onChange={(e) => setSubjectVal(e.target.value)} value={subjectVal} className="form-control " />
          </div>
        </div>
        <div className="form-group">
          <div className="col-sm-9 col-sm-offset-3">
            <Editor value={preEmailEditorState} options={'simple'} changed={onEditorStateChanged} height={'normal'} />

            <div className="text-muted">
              <h4>Message Keys:</h4>
              These keys will be replaced with the proper data when messages are sent out. Clicking a key will copy it so you can easily paste it into your message. Please note: Message keys are case
              sensitive and only work for the message and not the subject.
              <ul>
                <li>
                  <span
                    className="bold copy"
                    onClick={() => {
                      navigator.clipboard.writeText('[First-Name]');
                      toast.info(`Copied key.`);
                    }}>
                    [First-Name]
                  </span>{' '}
                </li>
                <li>
                  <span
                    className="bold copy"
                    onClick={() => {
                      navigator.clipboard.writeText('[Last-Name]');
                      toast.info(`Copied key.`);
                    }}>
                    [Last-Name]
                  </span>
                </li>
                <li>
                  <span
                    className="bold copy"
                    onClick={() => {
                      navigator.clipboard.writeText('[Name]');
                      toast.info(`Copied key.`);
                    }}>
                    [Name]
                  </span>{' '}
                  will be replaced with recipients name.
                </li>
                <li>
                  <span
                    className="bold copy"
                    onClick={() => {
                      navigator.clipboard.writeText('[Email]');
                      toast.info(`Copied key.`);
                    }}>
                    [Email]
                  </span>
                </li>
                <li>
                  <span
                    className="bold copy"
                    onClick={() => {
                      navigator.clipboard.writeText('[Sign-In-Link]');
                      toast.info(`Copied key.`);
                    }}>
                    [Sign-In-Link]
                  </span>{' '}
                  this still requires users to enter their username and password.
                </li>

                {selectedRecipients.find((x) => x.value === 'students' || x.value === 'partyMembers') && (
                  <>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Entry-Title]');
                          toast.info(`Copied key.`);
                        }}>
                        [Entry-Title]
                      </span>
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Entry-Id]');
                          toast.info(`Copied key.`);
                        }}>
                        [Entry-Id]
                      </span>
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[School]');
                          toast.info(`Copied key.`);
                        }}>
                        [School]
                      </span>
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Teacher-First-Name]');
                          toast.info(`Copied key.`);
                        }}>
                        [Teacher-First-Name]
                      </span>
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Teacher-Last-Name]');
                          toast.info(`Copied key.`);
                        }}>
                        [Teacher-Last-Name]
                      </span>
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Teacher-Email-Name]');
                          toast.info(`Copied key.`);
                        }}>
                        [Teacher-Email-Name]
                      </span>
                    </li>
                    {props.Settings && props.Settings.usesParty && (
                      <li>
                        <span
                          className="bold copy"
                          onClick={() => {
                            navigator.clipboard.writeText('[Party-Member-Info]');
                            toast.info(`Copied key.`);
                          }}>
                          [Party-Member-Info]
                        </span>{' '}
                        will be replaced with a list of their party members.{' '}
                      </li>
                    )}
                  </>
                )}

                {props.Settings && props.Settings.showQuickSignInOption && (
                  <>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Sign-In-Token]');
                          toast.info(`Copied key.`);
                        }}>
                        [Sign-In-Token]
                      </span>{' '}
                      will be replaced with a quick sign-in key.
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Sign-In]');
                          toast.info(`Copied key.`);
                        }}>
                        [Sign-In]
                      </span>{' '}
                      will include a link the user can click to be automatically signed-in.
                    </li>
                  </>
                )}

                {selectedRecipients.find((x) => x.value === 'outstanding') && (
                  <li>
                    <span
                      className="bold copy"
                      onClick={() => {
                        navigator.clipboard.writeText('[Invoice]');
                        toast.info(`Copied key.`);
                      }}>
                      [Invoice]
                    </span>{' '}
                    will be replaced with link to invoice. (outstanding invoice only)
                  </li>
                )}
                {selectedRecipients.find(
                  (x) => x.value === 'judges' || x.value === 'judgesWithAssignments' || x.value === 'judgesWithAssignmentsNoScores' || x.value === 'judgesPlanningToParticipate',
                ) && (
                  <>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[Circle-Assignments]');
                          toast.info(`Copied key.`);
                        }}>
                        [Circle-Assignments]
                      </span>{' '}
                      will be replaced with a list of judging circle assignments.
                    </li>
                    {selectedRecipients.find((x) => x.value === 'judges') && (
                      <li>
                        <span
                          className="bold copy"
                          onClick={() => {
                            navigator.clipboard.writeText('[certificate-of-appreciation-link]');
                            toast.info(`Copied key.`);
                          }}>
                          [Certificate-of-Appreciation-Link]
                        </span>{' '}
                        will be replaced with a link to the certificate of appreciation.
                      </li>
                    )}
                  </>
                )}
                {selectedRecipients.find((x) => INTERVIEW_KEY_GROUPS.has(String(x.value))) && (
                  <li>
                    <span
                      className="bold copy"
                      onClick={() => {
                        navigator.clipboard.writeText('[Interview-Times]');
                        toast.info(`Copied key.`);
                      }}>
                      [Interview-Times]
                    </span>{' '}
                    will be replaced with upcoming interview date/time, entry id, and title. Recipients with no upcoming interviews are skipped.
                  </li>
                )}
                {selectedRecipients.find((x) => x.value === 'volunteers') && (
                  <>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[certificate-of-appreciation-link]');
                          toast.info(`Copied key.`);
                        }}>
                        [Certificate-of-Appreciation-Link]
                      </span>{' '}
                      will be replaced with a link to the certificate of appreciation.
                    </li>
                  </>
                )}
                {selectedRecipients.find((x) => x.value === 'tempParticipants') && (
                  <li>
                    <span
                      className="bold copy"
                      onClick={() => {
                        navigator.clipboard.writeText('[Continue-Register-Link]');
                        toast.info(`Copied key.`);
                      }}>
                      [Continue-Register-Link]
                    </span>
                  </li>
                )}

                {selectedRecipients.find((x) => SCHOOL_AFFILIATION_INVOICE_KEY_GROUPS.has(String(x.value))) && (
                  <li>
                    <span
                      className="bold copy"
                      onClick={() => {
                        navigator.clipboard.writeText('[Invoice]');
                        toast.info(`Copied key.`);
                      }}>
                      [Invoice]
                    </span>
                  </li>
                )}
                {selectedRecipients.find((x) => SCHOOL_AFFILIATION_KEY_GROUPS.has(String(x.value))) && (
                  <>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[School-Name]');
                          toast.info(`Copied key.`);
                        }}>
                        [School-Name]
                      </span>{' '}
                      will be replaced with the affiliated school name.
                    </li>
                    <li>
                      <span
                        className="bold copy"
                        onClick={() => {
                          navigator.clipboard.writeText('[School-Password]');
                          toast.info(`Copied key.`);
                        }}>
                        [School-Password]
                      </span>{' '}
                      will be replaced with the school affiliation update password.
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className="bumper-l bumper-out-l form-group row">
          <label className="control-label col-sm-3">Attach Files</label>
          <div className="col-sm-6">
            {existingAttachments.length > 0 && (
              <ul className="list-unstyled">
                {existingAttachments.map((a) => (
                  <li key={a.FileId}>
                    <a href={a.Url} target="_blank" rel="noreferrer">
                      {a.FileName}
                    </a>{' '}
                    <Button type="remove" iconOnly onClick={() => removeExistingAttachment(a.FileId)} title="Remove attachment" />
                  </li>
                ))}
              </ul>
            )}
            <input type="file" multiple className="form-control" onChange={onChange} />
            {validFiles.length > 0 && (
              <ul className="list-unstyled">
                {validFiles.map((f: any) => (
                  <li key={f.name}>
                    {f.name} <Button type="remove" iconOnly onClick={() => removeFile(f.name)} title="Remove attachment" />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="form-group row">
          <label className="control-label col-sm-3">When to Send</label>
          <div className="col-sm-9">
            <div className="btn-group">
              <button type="button" className={`btn btn-${scheduleMode === 'now' ? 'primary' : 'default'}`} onClick={() => setScheduleMode('now')}>
                Send Now
              </button>
              <button type="button" className={`btn btn-${scheduleMode === 'absolute' ? 'primary' : 'default'}`} onClick={() => setScheduleMode('absolute')}>
                Specific Date &amp; Time
              </button>
              <button type="button" className={`btn btn-${scheduleMode === 'relative' ? 'primary' : 'default'}`} onClick={() => setScheduleMode('relative')}>
                Relative to a System Date
              </button>
            </div>
          </div>
        </div>
        {scheduleMode === 'absolute' && (
          <div className="form-group row">
            <label className="control-label col-sm-3">Date &amp; Time (approximate)</label>
            <div className="col-sm-6">
              <input type="datetime-local" className="form-control" value={scheduledAt} min={getMinScheduleValue()} onChange={(e) => setScheduledAt(e.target.value)} />
            </div>
          </div>
        )}
        {scheduleMode === 'relative' && (
          <div className="form-group row">
            <label className="control-label col-sm-3">Relative Schedule</label>
            <div className="col-sm-9">
              <div className="row">
                <div className="col-sm-4">
                  <Select
                    onChange={(newValue: any) => setRelativeSystemDateKey(newValue?.value ?? '')}
                    value={SYSTEM_DATE_OPTIONS.find((x) => x.value === relativeSystemDateKey) ?? null}
                    options={SYSTEM_DATE_OPTIONS}
                    placeholder="System Date"
                    menuPlacement={'auto'}
                  />
                </div>
                <div className="col-sm-2">
                  <select className="form-control" value={relativeDirection} onChange={(e) => setRelativeDirection(e.target.value as 'before' | 'after')}>
                    <option value="before">Before</option>
                    <option value="after">After</option>
                  </select>
                </div>
                <div className="col-sm-2">
                  <input type="number" min={0} className="form-control" value={relativeDays} onChange={(e) => setRelativeDays(Math.max(0, parseInt(e.target.value) || 0))} />
                </div>
                <div className="col-sm-1 control-label">days</div>
              </div>
              <p className="text-muted">The exact send time is resolved each time the system checks - if the system date changes later, this schedule moves with it.</p>
            </div>
          </div>
        )}
      </div>

      <div className="col-sm-9 col-sm-offset-3">
        <button onClick={handlePreview} className="bumper-l bumper-out-l btn btn-primary">
          <i className="fas fa-paper-plane"></i> View Preview
        </button>{' '}
        <button onClick={handleSendOrSchedule} className="bumper-l bumper-out-l btn btn-success">
          <i className="fal fa-alarm-clock"></i> {editingPublicId ? 'Save Changes' : scheduleMode === 'now' ? 'Send Now' : 'Schedule Email'}
        </button>
      </div>
    </>
  );

  return (
    <div className="bumper-l">
      <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
        <TabList>
          <Tab>Bulk Mailer</Tab>
          <Tab>Scheduled</Tab>
          <Tab>History</Tab>
        </TabList>
        <TabPanel>{renderBulkMailer()}</TabPanel>
        <TabPanel>
          <ScheduledBulkEmailList
            onEditPending={handleEditPending}
            recipientCategories={mappedRecipients}
            schoolOptions={mappedSchoolList}
            entryStatusOptions={mappedEntryStatus()}
            tagOptions={combinedTags}
            categoryOptions={categoryOptions}
            gradeOptions={gradeOptions}
            divisionOptions={divisionOptions}
          />
        </TabPanel>
        <TabPanel>
          <BulkEmailHistory
            onSelectHistoryItem={handleHistoryItemSelected}
            recipientCategories={mappedRecipients}
            schoolOptions={mappedSchoolList}
            entryStatusOptions={mappedEntryStatus()}
            tagOptions={combinedTags}
            categoryOptions={categoryOptions}
            gradeOptions={gradeOptions}
            divisionOptions={divisionOptions}
          />
        </TabPanel>
      </Tabs>
      {isModalOpen && (
        <Modal setModalOpen={setModalOpenState} title={messageResults && !messageResults.IsPreview ? 'Sending Messages' : 'Message Preview'} size="xl">
          {messageResults ? (
            <div>
              {!messageResults.Finished && (
                <ProcessingAlert>
                  {messageResults.IsPreview
                    ? 'Building previews. This may take a few moments and will auto-update.'
                    : `Sending messages… Tried: ${messageResults.TriedCount ?? 0}, Sent: ${messageResults.SentCount ?? 0}, Errors: ${messageResults.ErrorsCount ?? 0}. This will auto-update.`}
                </ProcessingAlert>
              )}

              {messageResults.Finished && (
                <div className="flex-between">
                  <h3>{messageResults.IsPreview ? 'Preview Message' : 'Messages Sent'}</h3>

                  {messageResults.ToList.length > 0 && (
                    <div>
                      {currentMessage + 1} of {messageResults.ToList.length}
                      {'  '}
                      <div className="btn-group">
                        <button onClick={() => setCurrentMessage(currentMessage - 1)} disabled={currentMessage === 0} className="btn btn-default btn-xs">
                          <i className="fal fa-arrow-down"></i>
                        </button>
                        <button onClick={() => setCurrentMessage(currentMessage + 1)} disabled={currentMessage === messageResults.ToList.length - 1} className="btn btn-default  btn-xs">
                          <i className="fal fa-arrow-up"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <hr />
              {messageResults.Finished && currentMessage < (messageResults.ToList?.length ?? 0) ? (
                <div>
                  <div className="bulk-preview-details">
                    <span className="bulk-preview-email">
                      Email: <strong>{messageResults.ToList[currentMessage].Email}</strong>
                    </span>
                    {messageResults.ToList[currentMessage].AllowTextMessage && (
                      <span className="bulk-preview-phone">
                        Phone: <strong>{messageResults.ToList[currentMessage].Phone}</strong>
                      </span>
                    )}
                  </div>
                  <div className="bulk-preview-subject" title="Subject">
                    {messageResults.ToList[currentMessage].Subject}
                  </div>
                  <div className="bulk-preview-message" dangerouslySetInnerHTML={{ __html: messageResults.ToList[currentMessage].Body }}></div>
                </div>
              ) : messageResults?.Finished && (!messageResults?.ToList || messageResults.ToList.length === 0) ? (
                <div>
                  <br />
                  <h3>No Results</h3>
                  No messages were generated or sent.
                  <br />
                  <br />
                </div>
              ) : null}
            </div>
          ) : (
            <div>
              <ProcessingAlert>This may take a few moments. This will auto update as results come in.</ProcessingAlert>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ReduxMap(EmailToolContainer);
